import { Injectable, OnDestroy } from '@angular/core';
import { Cell, TerrainGrid } from '../models/terrain-grid';
import { grpc } from '@improbable-eng/grpc-web';
import { TerrainService } from '../generated/terrain_pb_service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, mergeMap } from 'rxjs';
import { AgentSettings } from '../interfaces/agent-settings';
import { Store } from '@ngxs/store';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { TerrainInspectDetails } from '../models/TerrainInspectDetails';
import { TerrainRequest, TerrainResponse } from '../generated/terrain_pb';
import { StudyArea } from '../interfaces/study-area';
import { IGeometryGeoJSON } from '../interfaces/geo';
import { TerrainLoadingStatus } from '../enums/terrain-loading-status';
import { TiffProcessingStatus } from '../models/TiffProcessingStatus';
import { Severity } from 'src/app/global/enums/severity';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { RequestHeaderService } from './request-header.service';
import { InactivityService } from './inactivity.service';

@Injectable({
  providedIn: 'root',
})
export class TerrainDataService implements OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private http: HttpClient;

  constructor(
    private httpClient: HttpClient,
    private store: Store,
    private requestHeaderService: RequestHeaderService,
    private inactivityService: InactivityService
  ) {
    this.http = this.httpClient;
  }
  /**
   * Subject to handle the request for terrain data
   */
  private currentRequest: grpc.Request | null = null;

  public loadData(simulationId: string): Observable<TerrainGrid> {
    if (this.currentRequest) {
      this.currentRequest.close();
    }

    // clear my existing terrain data
    this.terrainGrid = new TerrainGrid();

    const request: TerrainRequest = new TerrainRequest();
    request.setId(simulationId);

    this.store.dispatch(
      new PredictorModelActions.SetTerrainLoadingStatus(
        TerrainLoadingStatus.Downloading
      )
    );

    return this.requestHeaderService.getHeaderObservable().pipe(
      mergeMap((metadata) => {
        const terrainGridSubject: Subject<TerrainGrid> =
          new Subject<TerrainGrid>();
        this.inactivityService.resetInactivityTimer();

        this.currentRequest = grpc.invoke(TerrainService.Get, {
          request: request,
          host: '',
          metadata: new grpc.Metadata(metadata),
          onMessage: (message: TerrainResponse) => {
            if (message.getHasmetadata()) {
              this.terrainGrid = new TerrainGrid();
              this.terrainGrid.id = simulationId;
              this.terrainGrid.cellsize = message.getCellsize();
              this.terrainGrid.cols = message.getCols();
              this.terrainGrid.rows = message.getRows();
              this.terrainGrid.lowerleftx = message.getLowerleftx();
              this.terrainGrid.lowerlefty = message.getLowerlefty();
              this.terrainGrid.cellsList = [];
            } else {
              message.getCellsList().forEach((cell) => {
                const newCell: Cell = new Cell();
                newCell.hillshade = cell.getHillshade();
                this.terrainGrid.cellsList.push(newCell);
              });
            }
          },
          onEnd: (code: grpc.Code, message: string) => {
            if (code == grpc.Code.OK) {
              console.log('terrain grid data received successfully.');
              terrainGridSubject.next(this.terrainGrid);
              terrainGridSubject.complete();
              this.store.dispatch(
                new PredictorModelActions.SetTerrainLoadingStatus(
                  TerrainLoadingStatus.Done
                )
              );
            } else {
              terrainGridSubject.error(message);
              this.store.dispatch(
                new PredictorModelActions.SetTerrainLoadingStatus(
                  TerrainLoadingStatus.FailedOnDownload
                )
              );
              this.store.dispatch(
                new NotificationsActions.Toast(message, Severity.Error)
              );
            }
          },
        });

        return terrainGridSubject.asObservable();
      })
    );
  }

  public terrainGrid!: TerrainGrid;

  public getTerrainStatus(
    terrainId: string
  ): Observable<TiffProcessingStatus> {
    const endpoint: string = `/api/terrain/${terrainId}/status`;
    return this.http.get<TiffProcessingStatus>(endpoint);
  }

  public getStudyAreas(
    includeGlobal: boolean = false
  ): Observable<{ $id: number; $values: StudyArea[] }> {
    const endpoint: string = '/api/studyareas';
    let params: HttpParams = new HttpParams();
    if (includeGlobal) params = params.append('includeGlobal', 'true');

    return this.http.get<{ $id: number; $values: StudyArea[] }>(endpoint, {
      params,
    });
  }

  public getStudyArea(id: string): Observable<IGeometryGeoJSON> {
    const endpoint: string = `/api/studyareas/${id}/shape`;
    const headers: HttpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/json; charset=utf-8'
    );
    return this.http.get<IGeometryGeoJSON>(endpoint, { headers });
  }

  public getStudyAreaById(studyAreaId: string): Observable<IGeometryGeoJSON> {
    const endpoint: string = '/api/terrain/StudyAreas/' + studyAreaId;
    const headers: HttpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/json; charset=utf-8'
    );
    return this.http.get<IGeometryGeoJSON>(endpoint, { headers });
  }

  public getSimulationEnvironment(studyAreaName: string): Observable<{
    terrainId: string;
    simulationId: string;
    agentSettings: AgentSettings;
  }> {
    const endpoint: string =
      '/api/terrain/GetsimulationEnvironment/' + studyAreaName;
    const headers: HttpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/json; charset=utf-8'
    );
    return this.http.get<{
      terrainId: string;
      simulationId: string;
      agentSettings: AgentSettings;
    }>(endpoint, { headers });
  }

  public setAgentSettings(
    simulationId: string,
    agentSettings: AgentSettings
  ): Observable<object> {
    this.inactivityService.resetInactivityTimer();
    const endpoint: string = 'api/terrain/' + simulationId + '/AgentSettings';
    const headers: HttpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/json; charset=utf-8'
    );
    return this.http.put(endpoint, agentSettings, { headers: headers });
  }

  public inspectCell(
    simulationId: string,
    cellIndex: number
  ): Observable<TerrainInspectDetails> {
    this.inactivityService.resetInactivityTimer();
    const endpoint: string =
      'api/terrain/' + simulationId + '/cell/' + cellIndex;
    const headers: HttpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/json; charset=utf-8'
    );
    return this.http.get<TerrainInspectDetails>(endpoint, { headers });
  }

  public clearTerrain(requestId: string): Observable<object> {
    const formData: FormData = new FormData();
    formData.append('requestId', requestId);
    const endpoint: string = 'api/terrain/ClearSimulation';
    return this.http.post(endpoint, formData);
  }
}
